import constant from '../../../constants/constants';
import storageHelper from '../../../libs/storageHelper';

export const defaultLanes = [
  {
    id: `${constant.appointmentPhases.expected.id}`,
    title: constant.appointmentPhases.expected.name,
    style: {
      borderTop: `5px solid ${constant.appointmentPhases.expected.color}`,
    },
    cards: [],
  },
  {
    id: `${constant.appointmentPhases.checkIn.id}`,
    title: constant.appointmentPhases.checkIn.name,
    style: {
      borderTop: `5px solid ${constant.appointmentPhases.checkIn.color}`,
    },
    cards: [],
  },
  {
    id: `${constant.appointmentPhases.diagnostics.id}`,
    title: constant.appointmentPhases.diagnostics.name,
    style: {
      borderTop: `5px solid ${constant.appointmentPhases.diagnostics.color}`,
    },
    cards: [],
  },
  {
    id: `${constant.appointmentPhases.preparation.id}`,
    title: constant.appointmentPhases.preparation.name,
    style: {
      borderTop: `5px solid ${constant.appointmentPhases.preparation.color}`,
    },
    cards: [],
  },
  {
    id: `${constant.appointmentPhases.procedure.id}`,
    title: constant.appointmentPhases.procedure.name,
    style: {
      borderTop: `5px solid ${constant.appointmentPhases.procedure.color}`,
    },
    cards: [],
  },
  {
    id: `${constant.appointmentPhases.dental.id}`,
    title: constant.appointmentPhases.dental.name,
    style: {
      borderTop: `5px solid ${constant.appointmentPhases.dental.color}`,
    },
    cards: [],
  },
  {
    id: `${constant.appointmentPhases.surgery.id}`,
    title: constant.appointmentPhases.surgery.name,
    style: {
      borderTop: `5px solid ${constant.appointmentPhases.surgery.color}`,
    },
    cards: [],
  },
  {
    id: `${constant.appointmentPhases.recovery.id}`,
    title: constant.appointmentPhases.recovery.name,
    style: {
      borderTop: `5px solid ${constant.appointmentPhases.recovery.color}`,
    },
    cards: [],
  },
  {
    id: `${constant.appointmentPhases.hospitalized.id}`,
    title: constant.appointmentPhases.hospitalized.name,
    style: {
      borderTop: `5px solid ${constant.appointmentPhases.hospitalized.color}`,
    },
    cards: [],
  },
  {
    id: `${constant.appointmentPhases.boarding.id}`,
    title: constant.appointmentPhases.boarding.name,
    style: {
      borderTop: `5px solid ${constant.appointmentPhases.boarding.color}`,
    },
    cards: [],
  },
  {
    id: `${constant.appointmentPhases.discharge.id}`,
    title: constant.appointmentPhases.discharge.name,
    style: {
      borderTop: `5px solid ${constant.appointmentPhases.discharge.color}`,
    },
    cards: [],
  },
  {
    id: `${constant.appointmentPhases.completed.id}`,
    title: constant.appointmentPhases.completed.name,
    style: {
      borderTop: `5px solid ${constant.appointmentPhases.completed.color}`,
    },
    cards: [],
  },
];

export const boardingOnlyLanes = [
  constant.appointmentPhases.expected,
  constant.appointmentPhases.boarding,
  constant.appointmentPhases.completed,
].map(({ id, name, color }) => ({
  id: `${id}`,
  title: name,
  style: { borderTop: `5px solid ${color}` },
  cards: [],
}));

export const PTB_DEFAULT_SORT_FIELD = 'parent';
export const PTB_DEFAULT_SORT_ORDER = 1;

export const PTB_SORT_OPTIONS = [
  { value: 'parent', label: 'Parent', defaultOrder: 1 },
  { value: 'patient', label: 'Pet', defaultOrder: 1 },
];

export const PTB_FILTER_SEARCH_THRESHOLD = 8;

export const PTB_PHASE_REMEMBER_LABEL =
  'Keep this Phase display on this device';

export const PTB_RELOAD_EVENT = 'loyal-ptb-reload';

/** Ephemeral pin + highlight after Add Pet to Update Board */
export const PTB_NEWLY_ADDED_FEEDBACK_MS = 2 * 60 * 1000;

const EXPECTED_PHASE_ID = constant.appointmentPhases.expected.id;

export function getApplicableLanes(isBoardingOnly) {
  return isBoardingOnly ? boardingOnlyLanes : defaultLanes;
}

export function getApplicablePhaseIds(isBoardingOnly) {
  return getApplicableLanes(isBoardingOnly).map((lane) => +lane.id);
}

export function getDefaultHiddenPhaseIds(isBoardingOnly) {
  return isBoardingOnly ? [EXPECTED_PHASE_ID] : [];
}

export function getPhaseChipDefinitions(isBoardingOnly) {
  return getApplicableLanes(isBoardingOnly).map((lane) => {
    const id = +lane.id;
    const phase = Object.values(constant.appointmentPhases).find(
      (item) => item.id === id,
    );

    return {
      id,
      name: lane.title,
      color: phase?.color || lane.style?.borderTop?.replace('5px solid ', ''),
    };
  });
}

export function getVisibleLanes(lanes, hiddenPhaseIds = []) {
  const hidden = new Set((hiddenPhaseIds || []).map((id) => `${id}`));

  return lanes.filter((lane) => !hidden.has(`${lane.id}`));
}

export function sanitizeHiddenPhaseIds(hiddenPhaseIds, isBoardingOnly) {
  const applicable = new Set(getApplicablePhaseIds(isBoardingOnly));

  return [
    ...new Set(
      (hiddenPhaseIds || [])
        .map((id) => +id)
        .filter((id) => applicable.has(id)),
    ),
  ];
}

export function getPtbPhaseStorageKey(facilityId) {
  return `ptb-phase-display:${facilityId}`;
}

export function loadPtbPhaseDisplay(facilityId, isBoardingOnly) {
  const defaults = {
    remember: false,
    hiddenPhaseIds: getDefaultHiddenPhaseIds(isBoardingOnly),
  };

  if (!facilityId) {
    return defaults;
  }

  try {
    const stored = storageHelper.getFromStorage(
      getPtbPhaseStorageKey(facilityId),
    );

    if (!stored?.remember) {
      return defaults;
    }

    return {
      remember: true,
      hiddenPhaseIds: sanitizeHiddenPhaseIds(
        stored.hiddenPhaseIds,
        isBoardingOnly,
      ),
    };
  } catch {
    return defaults;
  }
}

export function savePtbPhaseDisplay(
  facilityId,
  { remember, hiddenPhaseIds },
  isBoardingOnly,
) {
  if (!facilityId) {
    return;
  }

  const key = getPtbPhaseStorageKey(facilityId);

  if (!remember) {
    storageHelper.removeFromStorage(key);
    return;
  }

  storageHelper.addToStorage(key, {
    remember: true,
    hiddenPhaseIds: sanitizeHiddenPhaseIds(hiddenPhaseIds, isBoardingOnly),
  });
}

export function buildPtbRequestParams({
  search = '',
  sortField = PTB_DEFAULT_SORT_FIELD,
  sortOrder = PTB_DEFAULT_SORT_ORDER,
  filters = {},
}) {
  const types = filters.type || [];
  const doctors = filters.doctor || [];

  return {
    search: (search || '').trim(),
    sort: sortField,
    sortBy: sortOrder === -1 ? -1 : 1,
    ...(types.length ? { types: types.join(',') } : {}),
    ...(doctors.length ? { doctors: doctors.join(',') } : {}),
  };
}

export function getPtbFilterCategories({
  appointmentTypes = [],
  doctors = [],
  isBoardingOnly = false,
}) {
  const typeChoices = appointmentTypes.map((type) => ({
    value: type._id,
    label: type.title,
  }));

  const categories = [
    {
      key: 'type',
      label: 'Type',
      choices: typeChoices,
    },
  ];

  if (!isBoardingOnly) {
    const doctorChoices = doctors.map((doctor) => ({
      value: doctor._id || doctor.doctorId,
      label: doctor.name,
    }));
    doctorChoices.push({ value: 'NA', label: 'N/A' });

    categories.push({
      key: 'doctor',
      label: 'Doctor',
      choices: doctorChoices,
    });
  }

  return categories;
}
